/**
 * MEDIA CONTRACT — profile pictures, feed images/video, chat images/video.
 *
 * Three parties have to agree about a piece of media and they cannot import
 * each other: the client that resizes and uploads it, the callable that grants
 * the upload, and `storage.rules`, which is written in a different language
 * entirely. Every number below is therefore stated ONCE here, and the two
 * places that cannot import it are called out by name so drift is at least
 * visible:
 *
 *   - app/src/lib/media.ts imports these constants. No literals there.
 *   - functions/src/media/mediaFns.ts imports these constants. No literals.
 *   - storage.rules CANNOT import anything. It repeats AVATAR_MAX_BYTES,
 *     MEDIA_MAX_BYTES and VIDEO_MAX_BYTES as literals, each with a comment
 *     naming this file. If you change a number here, change it there too —
 *     the rule is the only one of the three that is actually enforcement.
 *
 * WHY A BLURHASH LIVES ON THE DOCUMENT
 * A non-Pro viewer must see a blurred, locked placeholder. That blur cannot be
 * produced by blurring the real file on the client: a client that can fetch the
 * file in order to blur it is a client that can keep the original, and the
 * paywall becomes decoration. So the blur is a ~30-character string computed at
 * upload time and stored on the post/message document, which the non-Pro client
 * already reads. The real object is never requested — no egress, and nothing to
 * intercept. The paywall itself is `storage.rules`; the blurhash only makes the
 * locked state look deliberate.
 */

export type MediaKind = 'image' | 'video';

/**
 * A piece of media as referenced from a post or a chat message.
 *
 * `path` IS AN OBJECT PATH, NEVER A DOWNLOAD URL — deliberately. A Firebase
 * Storage download URL carries a token that bypasses Storage rules for anyone
 * who has the link, so storing one on a document that non-Pro clients can read
 * would hand out exactly what the tier check exists to withhold. Clients fetch
 * by path (getBlob / getBytes), which is evaluated against the rules on every
 * request.
 *
 * Everything on this type is safe for a non-Pro client to receive: dimensions
 * to reserve layout, a byte count, a duration, and a blur that cannot be
 * inverted. The bytes themselves are the only thing behind the tier check.
 */
export interface MediaRef {
  /** Storage object path, e.g. `media/{uid}/images/{objectId}.webp`. */
  path: string;
  kind: MediaKind;
  /** Pixel dimensions AFTER client downscaling — used to reserve layout. */
  width: number;
  height: number;
  /**
   * BlurHash string (see BLURHASH_MAX_CHARS). Required: a media document
   * without one renders as an empty hole for every non-Pro viewer, which is
   * most viewers.
   */
  blurhash: string;
  /** Size of the stored object in bytes. */
  bytes: number;
  /** Videos only. */
  durationMs?: number;
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/**
 * Profile picture: 256px, WebP, under 30KB — deliberately NOT high resolution.
 *
 * Discovery renders these by the dozen and egress, not storage, is the bill.
 * At 10k users browsing 20 cards a day that is 200k image fetches; every 10KB
 * of avatar is ~2GB/day, roughly a rupee a day at Cloud Storage egress rates,
 * and it recurs forever. 256px is enough for a 52-60px card avatar at 3x
 * density with room to spare.
 */
export const AVATAR_MAX_PX = 256;
export const AVATAR_MAX_BYTES = 30_000;

/**
 * Feed and chat images: longest edge 1280px, WebP, under 300KB.
 *
 * 1280px is a full-bleed image on a 3x phone screen. The client re-encodes to
 * hit the byte target; there is deliberately NO server-side transcoding
 * function — that is a per-upload compute bill and an operational surface, and
 * a canvas on the device does the same job for free.
 */
export const MEDIA_MAX_PX = 1280;
export const MEDIA_MAX_BYTES = 300_000;

/** Hard ceiling on video length. Enforced on the client and in the callable. */
export const VIDEO_MAX_MS = 15_000;

/**
 * Video byte cap: 6MB for 15 seconds ≈ 3.2 Mbps.
 *
 * Justification, since this is the one number with real room for argument:
 *  - We do NOT transcode (a transcoding function is a compute bill, a cold
 *    start and a queue at this stage), so whatever the phone recorded is what
 *    we store and serve. The cap is therefore a *capture quality* decision
 *    disguised as a byte count.
 *  - 3.2 Mbps is comfortably above a 720p30 h.264 phone recording (~2-3 Mbps)
 *    and comfortably BELOW a 1080p30 one (~17 Mbps, i.e. ~32MB for 15s).
 *    A 1080p clip will be refused, and the client says so in those words —
 *    which is the intended outcome, not a bug to route around.
 *  - Cost: at Cloud Storage egress rates (~₹10/GB out of asia-south1) one view
 *    of a 6MB video is ~6 paise, against ~0.3 paise for a capped image. Video
 *    is a 20x cost per view, which is survivable only because BOTH posting and
 *    viewing are Pro-only — the audience for every one of these bytes is
 *    paying. If viewing ever opens up, this number has to come down first.
 *
 * A phone that cannot record under the cap must be told to record at a lower
 * quality; silently accepting 32MB would be a per-view cost we cannot recover.
 */
export const VIDEO_MAX_BYTES = 6_000_000;

/**
 * Longest edge for a video, checked only to keep the placeholder honest — we
 * cannot resize the video itself without transcoding, so this is advisory and
 * is NOT enforced anywhere. The byte cap is the real bound.
 */
export const VIDEO_ADVISORY_MAX_PX = 1280;

/**
 * Upper bound on a stored blurhash. The string is `4 + 2 * nx * ny`
 * characters, so the 4x3 we encode is 28 and there is room to go to 4x4 (36)
 * later. The cap exists so nobody can stuff a document with a large string
 * that every reader of that feed then pays to download.
 */
export const BLURHASH_MAX_CHARS = 40;

/**
 * Immutable objects, cached for a year. Repeat views of the same feed image
 * then cost nothing at all, which matters far more than the first fetch.
 * The uploader MUST set this — Storage rules cannot inspect Cache-Control, so
 * forgetting it is silent and expensive. The upload grant returns it for
 * exactly that reason.
 */
export const MEDIA_CACHE_CONTROL = 'public, max-age=31536000, immutable';

/**
 * Accepted image types. Enumerated rather than `image/*` on purpose: SVG is an
 * image type that can carry script, and `image/*` would wave it through. There
 * is no reason for a user-uploaded photo to be an SVG.
 */
export const MEDIA_IMAGE_CONTENT_TYPES = ['image/webp', 'image/jpeg', 'image/png'] as const;

/** Accepted video containers. Stored as uploaded; nothing re-wraps these. */
export const MEDIA_VIDEO_CONTENT_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;

export type MediaContentType =
  | (typeof MEDIA_IMAGE_CONTENT_TYPES)[number]
  | (typeof MEDIA_VIDEO_CONTENT_TYPES)[number];

/** File extension per accepted content type — the server names every object. */
export const MEDIA_EXTENSIONS: Record<MediaContentType, string> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** Readable by any signed-in user — discovery needs them. Owner-writable. */
export const AVATAR_PATH_ROOT = 'avatars';

/** Readable only by Pro. Writable only by a Pro owner. See storage.rules. */
export const MEDIA_PATH_ROOT = 'media';

/**
 * Images and videos are separated in the path so `storage.rules` can apply a
 * different byte cap and content-type list to each. A rule cannot look inside
 * the object; the path is the only thing it can key on.
 */
export type MediaTarget = 'avatar' | 'media';

export function avatarPathPrefix(uid: string): string {
  return `${AVATAR_PATH_ROOT}/${uid}/`;
}

export function mediaPathPrefix(uid: string, kind: MediaKind): string {
  return `${MEDIA_PATH_ROOT}/${uid}/${kind === 'video' ? 'videos' : 'images'}/`;
}

export interface ParsedMediaPath {
  target: MediaTarget;
  uid: string;
  kind: MediaKind;
  /** Filename without extension — the server-generated object id. */
  objectId: string;
}

/**
 * Parse an object path back into its parts, or null if it is not one of ours.
 *
 * Used by the server to answer "does this path belong to the caller" without a
 * lookup, and by any post/message writer that has to reject a MediaRef
 * pointing at somebody else's object. Rejects traversal and empty segments.
 */
export function parseMediaPath(path: string): ParsedMediaPath | null {
  if (!path || path.includes('..') || path.startsWith('/') || path.includes('//')) return null;
  const parts = path.split('/');
  const safe = (s: string | undefined): s is string => Boolean(s && /^[A-Za-z0-9_.-]+$/.test(s));

  if (parts[0] === AVATAR_PATH_ROOT && parts.length === 3) {
    const [, uid, file] = parts;
    if (!safe(uid) || !safe(file)) return null;
    return { target: 'avatar', uid, kind: 'image', objectId: file.replace(/\.[^.]+$/, '') };
  }
  if (parts[0] === MEDIA_PATH_ROOT && parts.length === 4) {
    const [, uid, folder, file] = parts;
    if (!safe(uid) || !safe(file)) return null;
    if (folder !== 'images' && folder !== 'videos') return null;
    return {
      target: 'media',
      uid,
      kind: folder === 'videos' ? 'video' : 'image',
      objectId: file.replace(/\.[^.]+$/, ''),
    };
  }
  return null;
}

/** True when `path` is one of ours AND belongs to `uid`. */
export function isOwnedMediaPath(path: string, uid: string): boolean {
  return parseMediaPath(path)?.uid === uid;
}

// ---------------------------------------------------------------------------
// Limit checking — one implementation, both sides
// ---------------------------------------------------------------------------

/**
 * Why a piece of media was refused. Stable machine codes: the client maps them
 * to i18n strings, the callable returns them in `details`, and neither has to
 * parse an English sentence to know what happened.
 */
export type MediaLimitCode =
  | 'image-too-large'
  | 'avatar-too-large'
  | 'video-too-large'
  | 'video-too-long'
  | 'video-duration-unknown'
  | 'blurhash-missing'
  | 'blurhash-too-long'
  | 'content-type-not-allowed';

export interface MediaCandidate {
  kind: MediaKind;
  bytes: number;
  durationMs?: number;
  blurhash?: string;
  contentType?: string;
}

/**
 * The single limit check. Returns the first violation, or null.
 *
 * Called on the client to fail before an upload starts, and again in the
 * callable — where it is a courtesy, not a defence. The client's numbers are
 * self-reported and a modified client will lie about all of them; the byte cap
 * that actually binds is `request.resource.size` in storage.rules, which is
 * measured by Google rather than declared by the uploader. Duration has no
 * equivalent: nothing but a transcoder can measure it server-side, so a
 * modified client CAN store a 60-second video. The byte cap bounds it.
 */
export function mediaLimitViolation(
  candidate: MediaCandidate,
  target: MediaTarget = 'media',
): MediaLimitCode | null {
  const { kind, bytes, durationMs, blurhash, contentType } = candidate;

  if (contentType !== undefined) {
    const allowed: readonly string[] =
      kind === 'video' ? MEDIA_VIDEO_CONTENT_TYPES : MEDIA_IMAGE_CONTENT_TYPES;
    if (!allowed.includes(contentType)) return 'content-type-not-allowed';
  }

  if (blurhash !== undefined) {
    if (blurhash.length === 0) return 'blurhash-missing';
    if (blurhash.length > BLURHASH_MAX_CHARS) return 'blurhash-too-long';
  }

  if (kind === 'video') {
    if (bytes > VIDEO_MAX_BYTES) return 'video-too-large';
    if (durationMs === undefined || !Number.isFinite(durationMs) || durationMs <= 0) {
      return 'video-duration-unknown';
    }
    if (durationMs > VIDEO_MAX_MS) return 'video-too-long';
    return null;
  }

  if (target === 'avatar') return bytes > AVATAR_MAX_BYTES ? 'avatar-too-large' : null;
  return bytes > MEDIA_MAX_BYTES ? 'image-too-large' : null;
}

/**
 * Machine-readable reason on a callable error when the caller is not Pro.
 * The client keys its upgrade prompt off this, never off the message text.
 */
export const MEDIA_PRO_REQUIRED_REASON = 'media-pro-required';

/**
 * Video is NOT automatically checked, and the product must say so rather than
 * implying it is. The moderation provider accepts text and images; it does not
 * accept video, and we do not extract frames to fake coverage — sampling one
 * frame in fifteen seconds would be worse than honest silence, because it
 * would let us claim videos are checked.
 *
 * Carried on the upload grant so the composer can show the notice, and on the
 * moderation result so nothing downstream can mistake a video for checked.
 */
export const MEDIA_VIDEO_UNCHECKED_REASON = 'video-not-automatically-checked';
